import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiRequest } from '../lib/api';
import { setOrganizationScope } from '../utils/authHeaders';
import { setImpersonationReason } from '../security/tenantScope';
import { useAuth } from '../hooks/useAuth';

// Canonical persisted key for the active tenant/workspace. Kept in sync with the
// `active_tenant_id` key read by authHeaders when building request headers.
const ACTIVE_TENANT_ID_KEY = 'active_tenant_id';

const getActiveTenantId = (): string => {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(ACTIVE_TENANT_ID_KEY) || '';
};

const saveActiveTenantId = (tenantId: string): void => {
  if (typeof window === 'undefined') return;
  if (tenantId) {
    window.localStorage.setItem(ACTIVE_TENANT_ID_KEY, tenantId);
  } else {
    window.localStorage.removeItem(ACTIVE_TENANT_ID_KEY);
  }
};

export interface Tenant {
  tenant_id: string;
  name: string;
  tier: string;
  primary_contact?: string;
  is_active: boolean;
  created_at?: string;
}

interface TenantContextType {
  activeTenantId: string;
  activeTenant: Tenant | null;
  tenants: Tenant[];
  isLoading: boolean;
  userRole: string;
  switchTenant: (tenantId: string, impersonationReason?: string) => Promise<void>;
  createTenant: (name: string, tier: string, primaryContact?: string) => Promise<Tenant>;
  refreshTenants: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [activeTenantId, setActiveTenantState] = useState<string>(getActiveTenantId());
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [activeTenant, setActiveTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userRole, setUserRole] = useState<string>(localStorage.getItem('user_role') || 'super_admin');

  const fetchTenants = async () => {
    setIsLoading(true);
    try {
      const res = await apiRequest('/api/v1/tenants');
      if (res.ok) {
        const data: Tenant[] = await res.json();
        setTenants(data);
        const currentId = getActiveTenantId();
        const current = data.find(t => t.tenant_id === currentId) || data[0] || null;
        if (current) {
          setActiveTenant(current);
          setActiveTenantState(current.tenant_id);
          saveActiveTenantId(current.tenant_id);
          setOrganizationScope(current.tenant_id);
        }
      }
    } catch (e) {
      console.warn('Failed to load tenants:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const switchTenant = async (tenantId: string, impersonationReason?: string) => {
    try {
      const ownOrgId = user?.organization_id || null;
      const isCrossOrg = Boolean(ownOrgId && tenantId !== ownOrgId);

      // Switching into another organization is a super-admin impersonation, which the
      // backend only accepts with an X-Impersonation-Reason (recorded in the audit log).
      // The reason is captured by the caller before this runs; switching back to your own
      // workspace clears it.
      if (isCrossOrg) {
        const reason = (impersonationReason || '').trim();
        if (!reason) {
          // Guard: never enter another org without a reason. The switcher UI collects it,
          // so this only trips on a programmatic misuse.
          console.warn('Cross-organization switch attempted without a reason; ignoring.');
          return;
        }
        setImpersonationReason(reason);
      } else {
        setImpersonationReason(null);
      }

      setActiveTenantState(tenantId);
      saveActiveTenantId(tenantId);
      setOrganizationScope(tenantId);
      const match = tenants.find(t => t.tenant_id === tenantId) || null;
      setActiveTenant(match);
      window.location.reload();
    } catch (e) {
      console.error('Error switching tenant:', e);
    }
  };

  const createTenant = async (name: string, tier: string, primaryContact?: string): Promise<Tenant> => {
    const res = await apiRequest('/api/v1/tenants', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Role': userRole
      },
      body: JSON.stringify({ name, tier, primary_contact: primaryContact }),
    });

    if (!res.ok) {
      let errMsg = 'Failed to create tenant';
      try {
        const err = await res.json();
        errMsg = typeof err.detail === 'string' ? err.detail : (err.message || JSON.stringify(err.detail));
      } catch {
        errMsg = `HTTP ${res.status}: ${res.statusText}`;
      }
      throw new Error(errMsg);
    }

    const newTenant: Tenant = await res.json();
    await fetchTenants();
    return newTenant;
  };

  return (
    <TenantContext.Provider value={{
      activeTenantId,
      activeTenant,
      tenants,
      isLoading,
      userRole,
      switchTenant,
      createTenant,
      refreshTenants: fetchTenants,
    }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
